'use client'

import { EnhancedInput } from '@/components/atoms/enhanced-input'
import { PasswordInput } from '@/components/atoms/password-input'
import { useAuth } from '@/features/auth'
import { Loader2 } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'

export function LoginForm() {
	const { login, isLoading, error, clearError } = useAuth()
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [remember, setRemember] = useState(false)

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		clearError()
		await login({ email, password })
	}

	return (
		<section className="w-full">
			{/* El badge "WELCOME BACK" lo renderiza LoginScreen (con su triángulo). */}
			<form onSubmit={handleSubmit} className="mt-4 space-y-3">
				{error && (
					<p className="rounded-md bg-red-50 px-3 py-2 text-[11px] text-red-600">{error}</p>
				)}

				<EnhancedInput
					name="email"
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder="Usuario / Correo"
					disabled={isLoading}
					className="h-9 rounded-full border border-(--brand-secondary) bg-transparent px-4 text-[14px] text-(--brand-secondary) placeholder:text-(--brand-secondary)"
				/>

				<PasswordInput
					name="password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					placeholder="Contraseña"
					disabled={isLoading}
					className="h-9 rounded-full border border-(--brand-secondary) bg-transparent px-4 text-[14px] text-(--brand-secondary) placeholder:text-(--brand-secondary)"
				/>

				<div className="mt-3 flex items-center justify-between gap-3">
					<label className="flex items-center gap-2 text-[11px] text-(--brand-secondary)">
						<input
							type="checkbox"
							checked={remember}
							onChange={(e) => setRemember(e.target.checked)}
							className="h-3.75 w-3.75 rounded-full border border-(--brand-secondary) accent-(--brand-primary)"
						/>
						<span>Recordarme</span>
					</label>

					<Link
						href="/forgot-password"
						className="text-[11px] text-(--brand-primary) underline underline-offset-2"
					>
						Olvidé mi contraseña
					</Link>
				</div>

				<div className="mt-8 flex items-end justify-between">
					{/* Registro */}
					<div className="flex flex-col gap-1">
						<span className="text-[10px] tracking-[0.12em] text-(--brand-secondary) uppercase">
							¿Sin cuenta?
						</span>
						<Link
							href="/register"
							className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] text-(--brand-primary) uppercase transition-opacity hover:opacity-70"
						>
							<span>Regístrate</span>
							<ArrowRightMini />
						</Link>
					</div>

					{/* Ingresar */}
					<button type="submit" disabled={isLoading} className="inline-flex disabled:opacity-60">
						<span className="inline-flex flex-col items-end gap-2 text-right">
							<span className="flex items-center gap-2">
								{isLoading ? (
									<Loader2 className="h-4 w-4 animate-spin text-(--brand-primary)" />
								) : (
									<ArrowRightMini />
								)}
								<span className="text-[13px] font-semibold tracking-[0.22em] text-(--brand-secondary) uppercase">
									{isLoading ? 'Ingresando...' : 'Ingresar'}
								</span>
							</span>
							<span className="block h-0.75 w-25.5 bg-(--brand-primary)" />
						</span>
					</button>
				</div>
			</form>
		</section>
	)
}

function ArrowRightMini() {
	return (
		<svg
			width="14"
			height="10"
			viewBox="0 0 14 10"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			className="shrink-0"
		>
			<path d="M1 5H11" stroke="var(--brand-primary)" strokeWidth="1.8" strokeLinecap="square" />
			<path
				d="M8 1L12 5L8 9"
				stroke="var(--brand-primary)"
				strokeWidth="1.8"
				strokeLinecap="square"
				strokeLinejoin="miter"
			/>
		</svg>
	)
}
